import type { ReactNode } from "react";

/**
 * Shared building blocks used inside guide Body() functions. Keeps the JSX
 * inside each guide focused on content, not styling.
 */

export function Lead({ children }: { children: ReactNode }) {
  return <p className="mb-5 text-base text-muted-dark">{children}</p>;
}

export function H2({ children, id }: { children: ReactNode; id?: string }) {
  return <h2 id={id} className="mt-7 mb-3 text-lg font-semibold text-ink">{children}</h2>;
}

export function H3({ children, id }: { children: ReactNode; id?: string }) {
  return <h3 id={id} className="mt-5 mb-2 text-sm font-semibold uppercase tracking-wider text-muted-dark">{children}</h3>;
}

export function P({ children }: { children: ReactNode }) {
  return <p className="my-3 text-sm leading-relaxed text-ink">{children}</p>;
}

export function Ol({ children }: { children: ReactNode }) {
  return <ol className="my-3 list-decimal space-y-2 pl-6 text-sm leading-relaxed text-ink">{children}</ol>;
}
export function Ul({ children }: { children: ReactNode }) {
  return <ul className="my-3 list-disc space-y-1 pl-6 text-sm leading-relaxed text-ink">{children}</ul>;
}
export function Li({ children }: { children: ReactNode }) {
  return <li className="text-sm leading-relaxed">{children}</li>;
}

/** A link that opens an in-app route via the hash router. */
export function AppLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <a href={`#/${to.replace(/^\//, "")}`} className="font-medium text-brand-700 hover:underline">
      {children}
    </a>
  );
}

/** External link — opens in new tab. */
export function Ext({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-brand-700 hover:underline">
      {children} ↗
    </a>
  );
}

/** Inline code. */
export function C({ children }: { children: ReactNode }) {
  return <code className="rounded bg-line-soft px-1.5 py-0.5 font-mono text-2xs text-ink">{children}</code>;
}

/** Block code. */
export function Code({ children }: { children: ReactNode }) {
  return (
    <pre className="my-3 overflow-x-auto rounded-md bg-canvas-deep p-3 font-mono text-2xs leading-snug text-ink ring-1 ring-line">
      <code>{children}</code>
    </pre>
  );
}

/** Callout box — tip / warning / info. */
export function Callout({ kind = "info", title, children }: { kind?: "info" | "tip" | "warn" | "danger"; title?: string; children: ReactNode }) {
  const cls =
    kind === "warn"   ? "border-warn/30 bg-warn-soft/60 text-amber-900" :
    kind === "danger" ? "border-danger/30 bg-danger-soft text-danger" :
    kind === "tip"    ? "border-accent/30 bg-accent-soft text-emerald-900" :
                        "border-info/30 bg-info-soft text-blue-900";
  const labelCls =
    kind === "warn"   ? "text-amber-900" :
    kind === "danger" ? "text-danger" :
    kind === "tip"    ? "text-emerald-900" :
                        "text-blue-900";
  const defaultTitle =
    kind === "warn"   ? "Heads up" :
    kind === "danger" ? "Important" :
    kind === "tip"    ? "Tip" :
                        "Info";
  return (
    <div className={`my-4 rounded-lg border px-4 py-3 text-sm leading-relaxed ${cls}`}>
      <div className={`mb-1 text-2xs font-semibold uppercase tracking-wider ${labelCls}`}>{title ?? defaultTitle}</div>
      <div>{children}</div>
    </div>
  );
}

/** Step block — numbered step with optional code/screenshot. */
export function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div className="my-4 flex gap-3">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-2xs font-semibold text-white">{n}</div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 text-sm font-semibold text-ink">{title}</div>
        <div className="text-sm leading-relaxed text-ink">{children}</div>
      </div>
    </div>
  );
}
